'use client';

import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { useCreateServiceTypesBulkMutation } from '@/features/service-types/api/service-typesApi';
import { createServiceTypeSchema } from '@/features/service-types/schemas';
import { useAppSelector } from '@/store/hooks';
import { useWizardBrowserStorage } from '@web/hooks';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import type {
  BulkCreateServiceTypesPayload,
  CreateServiceTypePayload,
} from '@/features/service-types/types';

type Props = {
  onSuccess?: () => void;
};

type FormServiceType = {
  label: string;
  description: string;
  billable_rate: number | null;
};

const onboardingIntroCopy =
  'Service Types are the core categories of work in Net Net, like design, development, or strategy. They are not just labels - they structure how work is planned, assigned, estimated, tracked, and reported across the workspace. If Service Types are set up clearly, the rest of the system stays cleaner and easier to understand.' as const;

export function ServiceTypesSetupForm({ onSuccess }: Props) {
  const { currentUser, currentWorkspaceSlug } = useAppSelector((s) => s.auth);
  const wizardId = `${currentUser?.email || 'anon'}:${currentWorkspaceSlug || 'no-ws'}`;
  const wizard = useWizardBrowserStorage({
    wizardId,
    version: 1,
    storage: 'local',
  });

  const persisted = wizard.getStepData<FormServiceType[]>('service-types');
  const [forms, setForms] = useState<FormServiceType[]>(
    () =>
      persisted ?? [
        { label: '', description: '', billable_rate: null },
        { label: '', description: '', billable_rate: null },
        { label: '', description: '', billable_rate: null },
      ],
  );

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const [createServiceTypesBulk, { isLoading }] =
    useCreateServiceTypesBulkMutation();

  const formServiceTypeSchema = useMemo(
    () =>
      z.object({
        label: createServiceTypeSchema.shape.label,
        description: createServiceTypeSchema.shape.description,
        billable_rate: createServiceTypeSchema.shape.billable_rate
          .nullable()
          .refine((v) => v !== null, 'Billable rate is required'),
      }),
    [],
  );

  const isRowEmpty = (row: FormServiceType) =>
    row.label.trim() === '' &&
    row.description.trim() === '' &&
    row.billable_rate == null;

  const isValid = useMemo(() => {
    const requiredValid = [0, 1, 2].every(
      (i) => formServiceTypeSchema.safeParse(forms[i]).success,
    );
    const optionalValid = forms
      .slice(3)
      .every(
        (r) =>
          isRowEmpty(r) || formServiceTypeSchema.safeParse({ ...r }).success,
      );
    return requiredValid && optionalValid;
  }, [forms, formServiceTypeSchema]);

  const setField = <K extends keyof FormServiceType>(
    index: number,
    key: K,
    value: FormServiceType[K],
  ) => {
    setForms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value } as FormServiceType;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      const required = forms.slice(0, 3);
      const optional = forms.slice(3).filter((r) => !isRowEmpty(r));
      const toSubmit = [...required, ...optional];

      const items: CreateServiceTypePayload[] = toSubmit.map((f) => ({
        label: f.label,
        description: f.description,
        billable_rate: f.billable_rate ?? 0,
      }));

      const payload: BulkCreateServiceTypesPayload = {
        items,
        mode: 'transactional',
      };

      const res = await createServiceTypesBulk(payload);
      if ((res.data as any).success) {
        notify({
          type: 'success',
          message: (res as any).message || 'Service types created successfully',
        });

        onSuccess?.();
      } else {
        throw new Error((res.data as any).message);
      }
    } catch (error) {
      console.error('-------', error);
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      wizard.saveStep('service-types', forms);
    }, 400);
    return () => clearTimeout(t);
  }, [forms, wizard]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
      <div
        className="card-container-glass"
        style={{
          marginBottom: '0.5rem',
        }}
      >
        {onboardingIntroCopy}
      </div>

      {forms.map((f, idx) => (
        <div
          key={`service-type-${idx}`}
          style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: '1fr 2fr 1fr',
            alignItems: 'start',
          }}
        >
          <SmartInput
            type="text"
            name={`label-${idx}`}
            label={`Service Type ${idx + 1} Name`}
            value={f.label}
            onChange={(v) => setField(idx, 'label', v)}
            onValidateChange={(_, err) =>
              setErrors((e) => ({ ...e, [`${idx}.label`]: err || undefined }))
            }
            schema={
              idx < 3
                ? createServiceTypeSchema.shape.label
                : z
                    .string()
                    .refine(
                      (v) => v.length === 0 || v.length >= 2,
                      'Label is required',
                    )
            }
            error={errors[`${idx}.label`]}
          />
          <SmartInput
            type="textarea"
            name={`description-${idx}`}
            label={`Service Type ${idx + 1} Description`}
            value={f.description}
            onChange={(v) => setField(idx, 'description', v)}
            onValidateChange={(_, err) =>
              setErrors((e) => ({
                ...e,
                [`${idx}.description`]: err || undefined,
              }))
            }
            schema={
              idx < 3
                ? createServiceTypeSchema.shape.description
                : z
                    .string()
                    .refine(
                      (v) => v.length === 0 || v.length >= 2,
                      'Description is required',
                    )
            }
            error={errors[`${idx}.description`]}
          />
          <SmartInput
            type="number"
            name={`billable_rate-${idx}`}
            label={`Service Type ${idx + 1} Billable Rate`}
            value={f.billable_rate}
            onChange={(v) => setField(idx, 'billable_rate', v)}
            onValidateChange={(_, err) =>
              setErrors((e) => ({
                ...e,
                [`${idx}.billable_rate`]: err || undefined,
              }))
            }
            schema={
              idx < 3
                ? formServiceTypeSchema.shape.billable_rate
                : z.number().min(0).nullable()
            }
            error={errors[`${idx}.billable_rate`]}
            min={0}
            max={1000000}
            step={0.01}
            formatOnBlur
            asCurrency
          />
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
        <MainButton
          variant="info"
          ariaLabel="Add Service Type"
          onClick={() =>
            setForms((prev) => [
              ...prev,
              { label: '', description: '', billable_rate: null },
            ])
          }
        >
          Add another
        </MainButton>
        <MainButton type="submit" isLoading={isLoading} disabled={!isValid}>
          Create Service Types
        </MainButton>
      </div>
    </form>
  );
}
